import React, { useContext, useEffect, useState } from "react";
import Auth from "../../utils/AuthUtils";
import { showError, showSuccess } from "../../utils/toastUtils";
import AuthAPI from "../../api/authApi";
import { useLoader } from "../../utils/LoaderUtils";
import { UserContext } from "../../utils/UserContext";
import useNumericInput from "../../hooks/useNumericInput";
import useAlphabeticInput from "../../hooks/useAlphabeticInput";
import ImageUploader from "../../components/partials/ImageUploader";

function MyProfile() {
  const nameToDisplay = Auth.getUserDetails("name");
  useNumericInput();
  useAlphabeticInput();

  const { user, setUser } = useContext(UserContext);

  const { showLoader, hideLoader } = useLoader();
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [originalData, setOriginalData] = useState(null);

  const INITIAL_FORM_DATA = {
    email: "",
    name: "",
    mobile: "",
    username: "",
    image: "",
  };

  const [formData, setFormData] = useState(INITIAL_FORM_DATA);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleImageUploadSuccess = (type, url) => {
    setFormData((prev) => ({
      ...prev,
      [type]: url,
    }));
  };

  // page load hote hi data get karna hai

  useEffect(() => {
    document.title = "My Profile | AxeSell Ecommerce Solution";

    const fetchUserDetails = async () => {
      showLoader();

      try {
        const res = await AuthAPI.getProfile();

        if (res.code === 200) {
          hideLoader();

          const result = res.data;
          if (result) {
            setFormData({
              email: result.email,
              name: result.name,
              mobile: result.mobile,
              username: result.username,
              image: result.image,
            });

            setOriginalData({
              email: result.email,
              name: result.name,
              mobile: result.mobile,
              username: result.username,
              image: result.image,
            });
          }
        } else {
          showError("Failed to fetch user details");
        }
      } catch (err) {
        showError("Failed to fetch user details");
      }
    };

    fetchUserDetails();
  }, []);

  const getChangedFields = (original, current) => {
    const changed = {};

    Object.keys(current).forEach((key) => {
      if (current[key] !== original[key]) {
        // empty / null image ignore
        if (key === "image" && !current[key]) return;
        changed[key] = current[key];
      }
    });

    return changed;
  };

  const validate = () => {
    const mobileRegex = /^[6-9]\d{9}$/;

    const newErrors = {};
    if (!formData.name) {
      newErrors.name = "Name is required";
      showError("Name is required");
    }

    if (formData.mobile && formData.mobile.length < 10) {
      newErrors.mobile = "Enter Valid Mobile Number";
      showError("Enter Valid Mobile Number");
    } else if (!mobileRegex.test(formData.mobile)) {
      newErrors.mobile = "Mobile must be of 10 digits";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  //submit form
  const handleSubmit = async (e) => {
    showLoader();
    setIsLoading(true);

    e.preventDefault();

    if (!validate()) {
      setIsLoading(false);
      hideLoader();
      return;
    }

    const payload = getChangedFields(originalData, formData);

    if (Object.keys(payload).length === 0) {
      showError("No changes detected");
      setIsLoading(false);
      hideLoader();
      return;
    }

    try {
      const res = await AuthAPI.updateProfile(payload);

      if (res.code === 200) {
        showSuccess(res.message);

        const updatedUser = { ...user, ...payload };

        setUser(updatedUser);
        localStorage.setItem("user", JSON.stringify(updatedUser));
      } else {
        showError(res.message);
      }
    } catch (err) {
      setIsLoading(false);
      showError(err.response?.data?.message || "Something went wrong");
    } finally {
      setIsLoading(false);
      hideLoader();
    }
  };

  return (
    <>
      <div className="content">
        <div className="page-header">
          <div className="page-title">
            <h4>My Profile</h4>
            <h6>
              Manage your personal information, account settings, and
              preferences.
            </h6>
          </div>
        </div>
        <div className="card">
          <div className="card-header">
            <h4>Hey {nameToDisplay}, this is your space ✨</h4>
          </div>
          <div className="card-body profile-body">
            <h5 className="mb-2">
              <i className="ti ti-user text-primary me-1" />
              Basic Information
            </h5>
            <form method="post" onSubmit={handleSubmit}>
              <ImageUploader
                type="image"
                label="image"
                value={formData.image}
                onUploadSuccess={handleImageUploadSuccess}
                assetType="profiles"
              />

              <div className="row">
                <div className="col-lg-6 col-sm-12">
                  <div className="mb-3">
                    <label className="form-label">
                      Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      className="form-control only-alphabets"
                      value={formData.name}
                      onChange={handleChange}
                    />
                    {errors.name && (
                      <span className="errors text-danger">{errors.name}</span>
                    )}
                  </div>
                </div>
                <div className="col-lg-6 col-sm-12">
                  <div className="mb-3">
                    <label className="form-label">
                      Email<span className="text-danger">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      className="form-control"
                      value={formData.email}
                      onChange={handleChange}
                      readOnly
                    />
                  </div>
                </div>
                <div className="col-lg-6 col-sm-12">
                  <div className="mb-3">
                    <label className="form-label">
                      Mobile Number<span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      name="mobile"
                      className="form-control only-numeric"
                      maxLength={10}
                      value={formData.mobile}
                      onChange={handleChange}
                    />
                    {errors.mobile && (
                      <span className="errors text-danger">
                        {errors.mobile}
                      </span>
                    )}
                  </div>
                </div>
                <div className="col-lg-6 col-sm-12">
                  <div className="mb-3">
                    <label className="form-label">
                      User Name<span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      name="username"
                      className="form-control"
                      readOnly
                      value={formData.username}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="col-12 d-flex justify-content-end">
                  {!isLoading && (
                    <button
                      type="submit"
                      id="submitBtn"
                      className="btn btn-primary shadow-none"
                    >
                      Save Changes
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}

export default MyProfile;
